import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import {
  HeartPulse,
  BarChart3,
  Flag,
  SlidersHorizontal,
  Users,
  Trophy,
  Megaphone,
  Settings,
} from "lucide-react";
import { AuthProvider } from "./context/AuthContext";
import AdminShell from "./components/layout/AdminShell";
import Dashboard from "./pages/Dashboard";
import AdminGenesys from "./pages/AdminGenesys";
import AuthPage from "./pages/AuthPage";
import NotFound from "./pages/NotFound";
import TagReviewQueue from "./pages/TagReviewQueue";
import AuditLog from "./pages/AuditLog";
import ErrorLog from "./pages/ErrorLog";
import TagRules from "./pages/TagRules";
import BanlistManager from "./pages/BanlistManager";
import Documentation from "./pages/Documentation";
import ComingSoon from "./pages/ComingSoon";
import RequireAdmin from "./components/RequireAdmin";
import "./App.css";

// Nav items with no backend yet — still real routes, just an honest
// "not wired up" empty state instead of fake data. See ComingSoon.jsx.
const PLACEHOLDER_PAGES = {
  "app-health": {
    title: "App Health",
    subtitle: "Crash-free rate, API latency, and uptime at a glance.",
    icon: HeartPulse,
    description: "No health-check or crash-reporting pipeline exists yet — this will read from the error log + a future uptime check once built.",
    todo: "wire to error_logs aggregates + an uptime/health endpoint",
  },
  analytics: {
    title: "Analytics",
    subtitle: "Deeper engagement, retention, and funnel breakdowns.",
    icon: BarChart3,
    description: "The dashboard home already shows a basic engagement trend from /api/analytics/summary — this page will go deeper (retention, funnels, per-game breakdowns).",
    todo: "extend backend/analytics.py with retention + funnel queries",
  },
  "feature-flags": {
    title: "Feature Flags",
    subtitle: "Toggle features and roll out gradually without a release.",
    icon: Flag,
    description: "There is no feature-flag backend yet — flags, rollout percentages, and environments all need a new collection + API.",
    todo: "design feature_flags collection + CRUD API",
  },
  "remote-config": {
    title: "Remote Config",
    subtitle: "Push non-flag config values to clients live.",
    icon: SlidersHorizontal,
    description: "GENESYS Points is the only remote-config surface that exists today. This page will generalize that pattern to other config keys.",
    todo: "generalize backend/game_config.py beyond genesys",
  },
  users: {
    title: "Users",
    subtitle: "Search, inspect, and manage user accounts.",
    icon: Users,
    description: "backend/users.py only exposes self-service endpoints (/me/custom-tags) — there's no admin list/search/detail API for other users' accounts yet.",
    todo: "add admin-scoped GET /api/admin/users (list + detail)",
  },
  achievements: {
    title: "Achievements",
    subtitle: "Define and track in-app achievements.",
    icon: Trophy,
    description: "No achievements backend exists yet.",
    todo: "design achievements collection + API",
  },
  announcements: {
    title: "App Announcements",
    subtitle: "Push in-app banners and update notes.",
    icon: Megaphone,
    description: "No announcements backend exists yet.",
    todo: "design announcements collection + API",
  },
  settings: {
    title: "Settings",
    subtitle: "Admin account and panel preferences.",
    icon: Settings,
    description: "Nothing configurable here yet beyond your account, which is already managed via login/logout.",
    todo: "decide what belongs here (admin roles? notification prefs?)",
  },
};

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <div className="App min-h-screen">
          <Routes>
            <Route path="/login" element={<AuthPage />} />

            <Route
              path="/admin"
              element={
                <RequireAdmin>
                  <AdminShell />
                </RequireAdmin>
              }
            >
              <Route index element={<Navigate to="dashboard" replace />} />
              <Route path="dashboard" element={<Dashboard />} />
              <Route path="genesys" element={<AdminGenesys />} />
              <Route path="tag-review" element={<TagReviewQueue />} />
              <Route path="error-log" element={<ErrorLog />} />
              <Route path="audit-log" element={<AuditLog />} />
              <Route path="tag-rules" element={<TagRules />} />
              <Route path="banlists" element={<BanlistManager />} />
              <Route path="docs" element={<Documentation />} />
              {Object.entries(PLACEHOLDER_PAGES).map(([slug, props]) => (
                <Route key={slug} path={slug} element={<ComingSoon {...props} />} />
              ))}
            </Route>

            <Route path="/" element={<Navigate to="/admin/dashboard" replace />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </div>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
